import React, { useEffect, useState } from 'react';
import { Box, Button, FormControl, FormLabel, Stack, TextField } from '@mui/material';
import useBundleTranslation from 'i18n';
import { Popup, PopupSettings } from 'components/common/popup/Popup';
import { datasetAPI } from 'api/dataset';
import {
    FormComponentValue,
    FormControlProps,
    FormElementControlPropsType,
} from 'components/common/form/layout/control';
import { useQueryClient } from '@tanstack/react-query';
import { useFormState } from 'react-hook-form';
import moment from 'moment';
import DataPreview from './DataPreview';
import { useTheme } from '@mui/material/styles';
import IconMi from 'components/common/icon/IconMi';
import ReactHookFormController from 'components/common/form/layout/ReactHookFormController';
import { getPkId, prepareFormElementProps } from 'components/common/form/formTools';
import useCustomSimplifiedForm from 'components/common/form/hooks/useCustomSimplifiedForm';
import Alert from '@mui/material/Alert';
import { BodyLoadingPlaceholder } from 'components/common/loading-placeholder/LoadingPlaceholder';
import { FormComponentBuilder } from 'components/common/form/layout';

export interface FormControlCSVUploaderProps extends FormControlProps {
    pkId: string;
    saveHistoricalInstances: boolean;
    gridToRefresh: string;
    refreshFields: string[];
    updateFields: string[];
    dataPreviewUrl: string;
}

export default function CSVUploader({ controlProps }: FormElementControlPropsType<FormControlCSVUploaderProps>) {
    const { t } = useBundleTranslation(['app/editor/dataset']);
    const yesterday = moment().subtract(1, 'days').toISOString().slice(0, 10);
    const [showLoader, setShowLoader] = useState(false);
    const [stageOneOpen, setStageOneOpen] = useState(false);
    const [dataExistConfirmOpen, setDataExistConfirmOpen] = useState(false);
    const [stageTwoOpen, setStageTwoOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [delimiter, setDelimiter] = useState(',');
    const [columnsToAddList, setColumnsToAddList] = useState([]);
    const [columnsToRemoveList, setColumnsToRemoveList] = useState([]);
    const [showStepTwoWarning, setShowStepTwoWarning] = useState(false);
    const [stepTwoSubmitButton, setStepTwoSubmitButton] = useState(t('stage_two_text_load'));
    const [stepTwoConfirmed, setStepTwoConfirmed] = useState('0');
    const [showDataPreview, setShowDataPreview] = useState(false);
    const [dataPreviewIteration, setDataPreviewIteration] = useState(0);

    const appTheme = useTheme();
    const submitStageTwoDisabled = selectedFile === null || delimiter === '';
    const { isDirty } = useFormState({ control: controlProps.form.hookFormControl });
    const tab = document.querySelector('.active-tab-content') as HTMLElement;
    const headingHeight: number = appTheme.size.navbarHeight + appTheme.size.tabsHeight;
    const dataPreviewInitialHeight = 350;

    const queryClient = useQueryClient();

    const elementProps = useCustomSimplifiedForm({
        loadDataDate: yesterday,
    });

    const onFileChange = (files: FileList) => {
        if (files && files.length > 0) {
            setSelectedFile(files[0]);
        }
    };
    const stageOneSettings: PopupSettings = {
        title: t('stage_one_title'),
        textOK: t('stage_one_text_ok'),
        needTranslation: false,
        uid: 'load_data',
    };
    const stageTwoSettings: PopupSettings = {
        title: t('stage_two_title'),
        textOK: stepTwoSubmitButton,
        needTranslation: false,
        uid: 'load_from_file',
    };
    const dataExistConfirmSettings: PopupSettings = {
        title: t('stage_one_confirm_title'),
        textOK: t('stage_one_confirm_text_ok'),
        needTranslation: false,
    };
    const onStageOneSubmit = () => {
        setShowLoader(true);

        datasetAPI
            .checkInstanceDate(controlProps.pkId, elementProps.form.hookFormGetValues('loadDataDate'))
            .then((response) => {
                setShowLoader(false);
                if (response.status === 'OK') {
                    if (response.exist === 'Y') {
                        showDataExistConfirm();
                    } else {
                        showStageTwo();
                    }
                } else {
                    setStageOneOpen(false);
                }
            });
    };

    const showDataExistConfirm = () => {
        setStageOneOpen(false);
        setDataExistConfirmOpen(true);
    };

    const showStageTwo = () => {
        setStageOneOpen(false);
        setDataExistConfirmOpen(false);
        setStageTwoOpen(true);
    };

    const onStageTwoConfirm = () => {
        setShowLoader(true);
        const formData = new FormData();
        if (selectedFile) {
            formData.append('csvFile', selectedFile, selectedFile.name);
        }
        formData.append('confirmed', stepTwoConfirmed);
        formData.append('instance', elementProps.form.hookFormGetValues('loadDataDate'));
        formData.append('delimiter', delimiter);

        datasetAPI.import(controlProps.pkId, formData).then((response) => {
            setShowLoader(false);

            if (response.data.status === 'WARNING') {
                setColumnsToAddList(response.data.columnsToAdd ?? []);
                setColumnsToRemoveList(response.data.columnsToRemove ?? []);
                setShowStepTwoWarning(true);
                setStepTwoConfirmed('1');
                setStepTwoSubmitButton(t('stage_two_text_load_anyway'));
            }

            if (response.data.status === 'OK') {
                onStageTwoHide();
                queryClient.invalidateQueries({ queryKey: [controlProps.gridToRefresh] });

                if (controlProps.refreshFields.length > 0) {
                    controlProps.refreshFields.forEach((field) => {
                        const fieldControlProps = { ...controlProps };
                        fieldControlProps.name = field;
                        fieldControlProps.uid = field;

                        if (fieldControlProps.form.handleRefreshComponentData) {
                            fieldControlProps.form
                                .handleRefreshComponentData(fieldControlProps, (response: any) => {
                                    return response.data.data;
                                })
                                .then((response) => {
                                    if (
                                        controlProps.updateFields.includes(field) &&
                                        response.data.status === 'OK' &&
                                        response.data.data
                                    ) {
                                        const values = response.data.data as FormComponentValue[];
                                        const currentValue = controlProps.form.hookFormGetValues(field);

                                        if (currentValue > '') {
                                            return;
                                        }

                                        controlProps.form.hookFormSetValue(field, values[0].value, {
                                            shouldDirty: true,
                                        });
                                    }
                                });
                        }
                    });
                }

                if (response.data.updatedFields) {
                    for (const [key, value] of Object.entries(response.data.updatedFields)) {
                        controlProps.form.hookFormSetValue(key, value);
                    }
                }

                setDataPreviewIteration(dataPreviewIteration + 1);
                setShowDataPreview(true);
            }

            if (response.data.status === 'ERROR') {
                onStageTwoHide();
                alert(response.data.message);
            }
        });
    };

    const onStageTwoHide = () => {
        setStepTwoSubmitButton(t('stage_two_text_load'));
        setStepTwoConfirmed('0');
        setStageTwoOpen(false);
        setSelectedFile(null);
        setDelimiter(',');
        elementProps.form.hookFormSetValue('loadDataDate', yesterday);
        setShowStepTwoWarning(false);
        setColumnsToAddList([]);
        setColumnsToRemoveList([]);
    };

    const stepTwoWarningMessage = (
        <Box>
            <Box sx={{ fontWeight: 600 }}>{t('stage_two_warning_header')}</Box>
            {columnsToAddList.length > 0 && (
                <Box>
                    {t('stage_two_warning_columns_added')} {columnsToAddList.toString()}
                </Box>
            )}
            {columnsToRemoveList.length > 0 && (
                <Box>
                    {t('stage_two_warning_columns_removed')} {columnsToRemoveList.toString()}
                </Box>
            )}
        </Box>
    );

    const onStageOneHide = () => {
        setStageOneOpen(false);
        elementProps.form.hookFormSetValue('loadDataDate', yesterday);
    };

    const onLoadButtonClick = () => {
        if (isDirty) {
            controlProps.form.formSave().then(() => {
                setStageOneOpen(true);
            });
        } else {
            setStageOneOpen(true);
        }
    };

    const setContentHeight = (height: number) => {
        tab.style.height = window.innerHeight - height - headingHeight + 'px';
    };

    useEffect(() => {
        if (!tab) return;
        if (showDataPreview) {
            setContentHeight(dataPreviewInitialHeight);
        } else {
            tab.style.height = '';
        }
    }, [showDataPreview]);

    return (
        <>
            <BodyLoadingPlaceholder open={showLoader} />
            <Box>
                <Button
                    data-test={'load-data-button'}
                    variant={'light'}
                    startIcon={<IconMi icon={'folder'} sx={{ fontSize: 16 }} />}
                    onClick={onLoadButtonClick}
                >
                    {isDirty ? t('component_button_dirty') : t('component_button')}
                </Button>
            </Box>
            <Popup
                key={'stage-1'}
                settings={stageOneSettings}
                open={stageOneOpen}
                onHide={onStageOneHide}
                onConfirm={onStageOneSubmit}
                maxWidth={'popupMd'}
            >
                <Stack className={'form-components-indent'}>
                    {controlProps.saveHistoricalInstances && <Box>{t('stage_one_has_history')}</Box>}
                    <FormControl fullWidth>
                        <FormLabel>{t('stage_one_day_title')}</FormLabel>
                        <ReactHookFormController
                            elementProps={prepareFormElementProps({
                                ...elementProps,
                                component: {
                                    component: 'FormDatePicker',
                                    name: 'loadDataDate',
                                    props: {
                                        showTimeSelect: false,
                                        uid: 'load_data_dateselect',
                                    },
                                },
                            })}
                        />
                    </FormControl>
                </Stack>
            </Popup>
            <Popup
                key={'stage-1-confirm'}
                settings={dataExistConfirmSettings}
                open={dataExistConfirmOpen}
                onHide={() => setDataExistConfirmOpen(false)}
                onConfirm={showStageTwo}
                maxWidth={'popupSm'}
            >
                <Box>
                    {t('stage_one_warning_message', { date: elementProps.form.hookFormGetValues('loadDataDate') })}
                </Box>
            </Popup>
            <Popup
                key={'stage-2'}
                settings={stageTwoSettings}
                open={stageTwoOpen}
                onHide={onStageTwoHide}
                onConfirm={onStageTwoConfirm}
                okDisabled={submitStageTwoDisabled}
                maxWidth={'popupMd'}
            >
                <Stack className={'form-components-indent'}>
                    {showStepTwoWarning && (
                        <Box>
                            <Alert severity={'error'} variant={'standard'}>
                                {stepTwoWarningMessage}
                            </Alert>
                        </Box>
                    )}

                    <FormControl fullWidth>
                        <FormLabel>{t('stage_two_delimiter')}</FormLabel>
                        <Box sx={{ width: '72px' }}>
                            <TextField
                                fullWidth={true}
                                value={delimiter}
                                onChange={(event) => {
                                    setDelimiter(event.target.value);
                                }}
                            />
                        </Box>
                    </FormControl>

                    <FormControl fullWidth>
                        <FormLabel>{t('stage_two_file')}</FormLabel>
                        <Box>
                            <ReactHookFormController
                                elementProps={prepareFormElementProps({
                                    ...elementProps,
                                    component: {
                                        uid: 'upload_file',
                                        name: 'upload_file',
                                        component: 'UploadFile',
                                        props: {
                                            view: 'field',
                                            accept: '.csv',
                                            inputPlaceholder: t('choose_file'),
                                            onFilesChange: onFileChange,
                                        },
                                    },
                                })}
                            />
                        </Box>
                    </FormControl>

                    <Box>
                        <Alert severity={'info'} variant={'standard'}>
                            <Box>
                                <Box sx={{ fontWeight: 600 }}>{t('stage_two_description_header')}</Box>
                                <Box>{t('stage_two_description_row_1')}</Box>
                                <Box>{t('stage_two_description_row_2')}</Box>
                            </Box>
                        </Alert>
                    </Box>
                    <Box>
                        <Box component={'span'} sx={{ fontWeight: 600 }}>
                            {t('stage_two_note_header')}
                        </Box>{' '}
                        {t('stage_two_note_body')}
                    </Box>
                </Stack>
            </Popup>
            {showDataPreview && (
                <Box sx={{ position: 'fixed', inset: 'auto 0 0 0', zIndex: 1 }}>
                    <DataPreview
                        show={showDataPreview}
                        setShow={setShowDataPreview}
                        defaultHeight={dataPreviewInitialHeight}
                        onResize={(e, dir, ref, delta) => {
                            setContentHeight(ref.clientHeight);
                        }}
                        showCloseButton
                        pkId={controlProps.pkId}
                        uid={'data-preview-' + controlProps.pkId + '-' + dataPreviewIteration}
                        gridConfigUrl={controlProps.dataPreviewUrl}
                    />
                </Box>
            )}
        </>
    );
}

export class CSVUploaderBuilder extends FormComponentBuilder {
    prepareProps(): FormControlCSVUploaderProps {
        return {
            ...this.controlProps,
            pkId: getPkId(this.elementProps.pk, this.elementProps.urlParams),
            saveHistoricalInstances: this.elementProps.data?.save_historical_instances_ind == 'Y',
            gridToRefresh: this.elementProps.componentProps?.gridToRefresh ?? '',
            refreshFields: this.elementProps.componentProps?.refreshFields ?? [],
            updateFields: this.elementProps.componentProps?.updateFields ?? [],
            dataPreviewUrl: this.elementProps.componentProps?.dataPreviewUrl ?? '',
        };
    }
}
